import { useMemo, useState } from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@design-system';
import { useUnderwritingPromptVersion } from '../hooks/use-wizard-underwriting-guidelines';
import type { UnderwritingPromptVersionListItem } from '../api/api';
import { UnifiedDiff } from './UnifiedDiff';

interface VersionHistoryTableProps {
  versions: UnderwritingPromptVersionListItem[];
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function truncate(text: string | null, max = 80): string {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export function VersionHistoryTable({ versions }: VersionHistoryTableProps) {
  const [expandedVersionId, setExpandedVersionId] = useState<string | null>(null);

  const sorted = useMemo(
    () => [...versions].sort((a, b) => b.versionNumber - a.versionNumber),
    [versions],
  );

  const expandedVersion = useUnderwritingPromptVersion(expandedVersionId);
  const predecessorId = useMemo(() => {
    if (!expandedVersionId) return null;
    const idx = sorted.findIndex(v => v.id === expandedVersionId);
    if (idx < 0 || idx + 1 >= sorted.length) return null;
    return sorted[idx + 1].id;
  }, [expandedVersionId, sorted]);
  const predecessorVersion = useUnderwritingPromptVersion(predecessorId);

  if (sorted.length === 0) {
    return <div className="text-sm text-gray-500">No version history yet.</div>;
  }

  return (
    <div className="space-y-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">Version</TableHead>
            <TableHead className="w-24">Status</TableHead>
            <TableHead>Published at</TableHead>
            <TableHead>Published by</TableHead>
            <TableHead>Notes</TableHead>
            <TableHead className="w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map(v => {
            const isOpen = expandedVersionId === v.id;
            return (
              <TableRow key={v.id} className={isOpen ? 'bg-gray-50' : undefined}>
                <TableCell className="font-mono text-sm">v{v.versionNumber}</TableCell>
                <TableCell>
                  <span
                    className={
                      v.status === 'published'
                        ? 'text-green-700'
                        : v.status === 'draft'
                          ? 'text-amber-700'
                          : 'text-gray-500'
                    }
                  >
                    {v.status}
                  </span>
                </TableCell>
                <TableCell className="text-sm">{formatDateTime(v.publishedAt)}</TableCell>
                <TableCell className="text-sm">{v.publishedByName ?? '—'}</TableCell>
                <TableCell className="text-sm text-gray-700">{truncate(v.notes)}</TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedVersionId(isOpen ? null : v.id)}
                  >
                    {isOpen ? 'Hide' : 'View'}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {expandedVersionId && expandedVersion.data && (
        <div className="space-y-2 rounded border border-gray-200 bg-white p-4">
          <div className="text-sm font-medium">
            v{expandedVersion.data.versionNumber} content
          </div>
          <pre className="max-h-[320px] overflow-auto rounded bg-gray-50 p-3 font-mono text-xs">
            {expandedVersion.data.content}
          </pre>
          {predecessorVersion.data && (
            <>
              <div className="text-sm font-medium">
                Diff vs v{predecessorVersion.data.versionNumber}
              </div>
              <UnifiedDiff
                before={predecessorVersion.data.content}
                after={expandedVersion.data.content}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
